import React, { useState } from 'react';
import { X, AlertTriangle, UserX, Trash2, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';
import { userApi } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const DeleteAccountModal = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const CONFIRM_KEYWORD = 'DELETE';
  const isConfirmed = confirmationInput.trim() === CONFIRM_KEYWORD;

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!isConfirmed) return;

    setError('');
    setIsSubmitting(true);

    try {
      const response = await userApi.deleteAccount();
      // api interceptor unwraps response.data, so response is { success: true, message: '...' }
      const isSuccess = response?.success === true || response?.data?.success === true || response?.status === 200 || (response && !response?.error && response?.success !== false);
      if (isSuccess && response?.success !== false) {
        toast.success(response?.message || response?.data?.message || 'Account and all associated records deleted permanently.');
        onClose();

        // Immediate logout and cleanup
        setTimeout(() => {
          logout('account_deleted');
        }, 1000);
      } else {
        const errorMsg = response?.message || response?.data?.message || 'Failed to delete account.';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsSubmitting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete account.';
      setError(msg);
      toast.error(msg);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div 
        className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-red-200 dark:border-red-900/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-red-100 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/60 text-red-600 dark:text-red-400 rounded-xl">
              <UserX size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-900 dark:text-red-300">Delete Account Permanently</h2>
              <p className="text-xs text-red-700/80 dark:text-red-400/80">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-2.5 p-3 bg-red-100 dark:bg-red-950/60 border border-red-300 dark:border-red-800 rounded-xl text-red-800 dark:text-red-200 text-xs">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Warning Banner */}
          <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl">
            <div className="flex items-start gap-3">
              <ShieldAlert size={22} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-red-900 dark:text-red-200 space-y-2">
                <p className="font-bold text-sm text-red-700 dark:text-red-400">
                  ⚠️ This action is permanent and completely irreversible!
                </p>
                <p className="leading-relaxed">
                  Proceeding with this request will immediately and permanently erase all your data from SchemaGraphos and remove your identity from Auth0:
                </p>
                <ul className="list-disc pl-4 space-y-1 text-red-800 dark:text-red-300">
                  <li><strong>All Database Models & Diagrams</strong> created in your workspace</li>
                  <li><strong>All Version Snapshots & History</strong></li>
                  <li><strong>All Custom Domain Templates & User-Defined Types (UDTs)</strong></li>
                  <li><strong>All AI Assistant Chat Histories, Settings & Encrypted Keys</strong></li>
                  <li><strong>Your User Profile & Authentication Account in Auth0</strong></li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleDeleteAccount} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                To confirm deletion, please type <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> below:
              </label>
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                placeholder="Type DELETE to confirm"
                disabled={isSubmitting}
                className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="pt-3 flex items-center justify-end gap-3 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isConfirmed || isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Deleting all data...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Permanently Delete My Account</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
